const normalizeIdentifier = value => String(value || '').replace(/[^a-z0-9]/gi, '').toLowerCase();

const getWidgetFilename = widget => {
  const widgetUrl = String(widget?.widgetUrl || '').split('?')[0];
  return widgetUrl.split('/').pop().toLowerCase();
};

const isRetailMediaTemplate = template => {
  const identifiers = [template?.id, template?.name, template?.displayName].map(normalizeIdentifier);
  return identifiers.includes('analyticsmobile') || identifiers.some(identifier => identifier.includes('retailmedia'));
};

const getEstimatedImpressionsViewId = (widget, template) => {
  if (template?.isCms) return null;
  if (widget?.id === 'estimated-impression') return 'traffic_estimated_impressions';
  const filename = getWidgetFilename(widget);
  if (filename === 'impressions.json') return 'estimated_impressions_digit';
  if (filename === 'impressionsline.json') return 'estimated_impressions_time_series';
  return null;
};

const getSelectedSiteIds = (selectedSites = []) =>
  Array.from(
    new Set(
      selectedSites
        .map(site => (typeof site === 'string' ? site : site?.id))
        .filter(Boolean)
    )
  );

const getGranularity = interval => {
  const intervalId = typeof interval === 'string' ? interval : interval?.id;
  const granularities = {
    hour: 'HOUR',
    day: 'DAY',
    week: 'WEEK',
    monday_week: 'WEEK',
    month: 'MONTH',
    year: 'MONTH'
  };

  return granularities[intervalId] || 'DAY';
};

const getRequestPeriods = ({ startDate, endDate }, timeZone) => {
  const start = new Date(startDate);
  const selectedEnd = new Date(endDate);
  const end = new Date(selectedEnd);
  end.setSeconds(end.getSeconds() + 1, 0);

  const duration = end.getTime() - start.getTime();
  const previousStart = new Date(start.getTime() - duration);
  const period = (periodStart, periodEnd) => ({
    startTime: periodStart.toISOString(),
    endTime: periodEnd.toISOString(),
    timeZone: timeZone || 'UTC'
  });

  return {
    current: period(start, end),
    previous: period(previousStart, start)
  };
};

const getTimeSeriesLabel = (timestamp, granularity, timeZone) => {
  const options =
    granularity === 'HOUR'
      ? { month: '2-digit', day: '2-digit', hour: 'numeric' }
      : granularity === 'MONTH'
        ? { year: 'numeric', month: '2-digit' }
        : { year: '2-digit', month: '2-digit', day: '2-digit' };

  return new Intl.DateTimeFormat('en-US', { ...options, timeZone: timeZone || 'UTC' }).format(
    new Date(timestamp)
  );
};

const getScalarData = (response, label = 'Impressions') => [
  { label, value: Number(response?.scalar?.value || 0) }
];

const getTimeSeriesData = (response, granularity, timeZone) => {
  const series = response?.timeSeries?.series || [];
  const timestamps = Array.from(
    new Set(series.flatMap(item => (item.points || []).map(point => point.timestamp)).filter(Boolean))
  ).sort();
  const labelsByTimestamp = Object.fromEntries(
    timestamps.map(timestamp => [timestamp, getTimeSeriesLabel(timestamp, granularity, timeZone)])
  );

  return {
    categories: timestamps.map(timestamp => labelsByTimestamp[timestamp]),
    data: series.map(item => ({
      label: item.label || item.key,
      value: (item.points || []).map(point => ({
        label: labelsByTimestamp[point.timestamp],
        value: Number(point.value || 0)
      }))
    }))
  };
};

export {
  getEstimatedImpressionsViewId,
  getGranularity,
  getRequestPeriods,
  getSelectedSiteIds,
  getScalarData,
  getTimeSeriesData,
  isRetailMediaTemplate
};
